import type { SpatialDatasetProcessingStatus } from "../types";
import { isApiMedia } from "../utilities/mediaAsset";

/**
 * One status story for an uploaded asset (a spatial dataset, and media once it
 * has the same fields). The drawer tile, the list chip and the detail card all
 * read their label, colour and text from here, and every poll asks
 * `isLiveAsset` whether to keep polling.
 *
 * `processing_status` is the truth: pending (row made, no bytes yet) →
 * uploading (multipart initialized) → queued (upload complete) → processing →
 * completed | failed. Media uses only pending → uploading → completed | failed;
 * `upload_details.status` stands in where a response has no `processing_status`.
 */

export type AssetStatus = SpatialDatasetProcessingStatus;

export interface AssetStatusSource {
  processing_status?: string | null;
  processing_error?: string | null;
  upload_details?: { status?: string | null } | null;
}

export type AssetStatusTone = "default" | "info" | "warning" | "success" | "error";

export interface AssetStatusPresentation {
  status: AssetStatus;
  /** Chip and tile label. */
  label: string;
  /** What is happening, for the detail card. Empty once the asset is ready. */
  description: string;
  tone: AssetStatusTone;
}

const STATUSES: readonly AssetStatus[] = ["pending", "uploading", "queued", "processing", "completed", "failed"];

const PRESENTATION: Record<AssetStatus, Omit<AssetStatusPresentation, "status">> = {
  pending: { label: "Waiting for upload", description: "No file has been uploaded yet.", tone: "default" },
  uploading: { label: "Uploading", description: "The file is uploading.", tone: "info" },
  queued: { label: "Queued", description: "Uploaded. Waiting for conversion to start.", tone: "warning" },
  processing: { label: "Processing", description: "Converting the file.", tone: "info" },
  completed: { label: "Ready", description: "", tone: "success" },
  failed: { label: "Failed", description: "Conversion failed.", tone: "error" }
};

export const getAssetStatus = (asset: AssetStatusSource | null | undefined): AssetStatus => {
  const status = asset?.processing_status || asset?.upload_details?.status;
  return STATUSES.includes(status as AssetStatus) ? (status as AssetStatus) : "pending";
};

export const getAssetStatusPresentation = (asset: AssetStatusSource | null | undefined): AssetStatusPresentation => {
  const status = getAssetStatus(asset);
  const presentation = { status, ...PRESENTATION[status] };
  if (status === "failed" && asset?.processing_error) presentation.description = asset.processing_error;
  return presentation;
};

/** How often a view showing a live asset refetches it. */
export const ASSET_POLL_INTERVAL_MS = 5_000;

/**
 * Whether the asset's status can change without anyone acting on it, so a view
 * showing it should poll. Only `queued` and `processing` can: the conversion
 * worker moves them. `pending` and `uploading` move only when the browser
 * sending the file calls initialize or complete, and that browser writes the new
 * status into its own caches (`applyAssetStatus`). Polling them would only keep
 * every open list refetching a row whose upload may never finish.
 */
export const isLiveAsset = (asset: AssetStatusSource | null | undefined): boolean => {
  const status = getAssetStatus(asset);
  return status === "queued" || status === "processing";
};

type StatusTarget = AssetStatusSource & { display_url?: string | null; dataset_type?: string | null };

/**
 * Whether a tile or row shows the asset's status instead of the asset.
 * - A map dataset, until it is completed and has a `display_url`. A tabular one
 *   never gets a `display_url`, so completed is enough.
 * - API media with a `processing_status`, until it is completed. A download-only
 *   file (zip, docx, csv) never gets a `display_url`, so completed is enough there too.
 * - Anything else (CM rows, external-data media) has no asset status to show.
 */
export const showsAssetStatus = (item: StatusTarget | null | undefined, entityType?: string): boolean => {
  if (!item) return false;
  const status = getAssetStatus(item);
  if (entityType === "spatial-dataset") return status !== "completed" || (item.dataset_type !== "tabular" && !item.display_url);
  if (isApiMedia(item)) return Boolean(item.processing_status) && status !== "completed";
  return false;
};
